import posthog from 'posthog-js';

/**
 * O PostHog dos dois apps, iniciado do mesmo jeito.
 *
 * **O que este arquivo existe para resolver é o subdomínio.** `titularis.com.br`
 * e `app.titularis.com.br` são origens diferentes para o navegador: com a
 * persistência em `localStorage`, como o `apps/app` fazia sozinho até
 * 13/09/2026, a mesma pessoa virava duas e o funil "leu a landing → criou
 * conta" nunca fechava. O par `localStorage+cookie` mais
 * `cross_subdomain_cookie` é a correção, e é a razão de o início morar num
 * pacote em vez de ser copiado nos dois lados - copiado, um dos dois
 * divergiria, e o número errado seria silencioso.
 */
let ativo = false;

export interface OpcoesDoPostHog {
  chave?: string;
  host?: string;
  /**
   * Quem conta a tela.
   *
   * `automatico` entrega isso ao PostHog, pelo pacote de padrões atual, que já
   * sabe seguir troca de rota em aplicação de página única. É o que o
   * `apps/site` usa: a landing não tem política própria de tela e não ganharia
   * nada reimplementando uma.
   *
   * `manual` desliga a captura e deixa o app chamar quando quiser. É a decisão
   * vigente do `apps/app`, e o motivo dela está lá: as telas são de rota do
   * lado do cliente e a instrumentação de lá já escolheu o momento de cada
   * uma. Trocar isso por aqui mudaria a série histórica do app sem que
   * ninguém pedisse.
   */
  pageview?: 'automatico' | 'manual';
  /**
   * Gravar a navegação desta aplicação?
   *
   * Desligado por padrão: quem liga diz por escrito, no próprio app, por que
   * aquela tela pode ir para uma ferramenta de terceiro.
   *
   * **As duas aplicações gravam desde 22/09/2026.** O `apps/site` gravava
   * sozinho porque a landing é texto público, e o `apps/app` ficava de fora
   * porque gravar a tela dele era despejar o produto no PostHog sem a marca do
   * leitor que o G-WATERMARK põe em tudo o que se entrega. O que mudou foi o
   * início do GTM: com pouquíssimos alunos, ver cada um estudar vale mais do que
   * qualquer agregado, e o obstáculo tinha conserto - o material autoral sai da
   * gravação por construção (ver `ATRIBUTO_DE_MATERIAL_AUTORAL`), e o resto da
   * tela entra.
   *
   * Ligado, os campos entram mascarados. O texto não: mascará-lo deixaria a
   * gravação inútil justamente para a pergunta que ela responde - onde a pessoa
   * travou -, e o que sobra de texto depois do bloqueio é interface, enunciado
   * de banca e lei, nada disso nosso.
   */
  gravarSessao?: boolean;
}

/**
 * O atributo que tira um elemento da gravação de sessão, inteiro.
 *
 * **É a condição para o `apps/app` gravar.** O G-WATERMARK diz que nenhuma
 * telemetria carrega o texto do material, e uma gravação de tela é telemetria
 * que carrega tudo o que se vê. O elemento marcado vira, no replay, um
 * retângulo vazio do mesmo tamanho: dá para ver que a pessoa rolou, parou e
 * clicou ali, e não dá para ler uma palavra.
 *
 * **Bloqueio, e não máscara de texto.** A máscara troca as letras por
 * asteriscos e deixa passar o resto - imagem, tabela, a forma do parágrafo -, e
 * o G-WATERMARK fala de todo pixel de material próprio, não só do texto.
 *
 * Quem marca é o `ConteudoRico` com `protegido`, que já era a linha entre o que
 * é nosso e o que é da banca (D-53). Material autoral novo que passe por ele
 * sai da gravação sem ninguém lembrar disto; o que for renderizado por fora
 * precisa do atributo à mão.
 */
export const ATRIBUTO_DE_MATERIAL_AUTORAL = 'data-material-autoral';

export function iniciarPostHog(opcoes: OpcoesDoPostHog): boolean {
  // Desenvolvimento e prévias precisam funcionar sem entrar nos números de produção.
  if (!opcoes.chave) return false;
  if (ativo) return true;

  const automatico = (opcoes.pageview ?? 'automatico') === 'automatico';

  try {
    posthog.init(opcoes.chave, {
      api_host: opcoes.host ?? 'https://us.i.posthog.com',
      // Só o cookie compartilhado acompanha a mesma pessoa da landing até o app.
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: true,
      /*
       * **Sem `__preview_cookie_wins_on_conflict`, e é decisão.** A opção
       * existe na 1.407 e faz o cookie vencer quando o `localStorage` do app
       * traz um id diferente - o caso de quem já usou o app antes desta
       * mudança e só depois passou pela landing.
       *
       * Fica de fora por dois motivos. O primeiro é que ela é indocumentada e
       * marcada como prévia no próprio nome: some numa versão menor e leva o
       * funil junto, calada. O segundo é mais forte - **nesse conflito o
       * `localStorage` está certo**. Ele guarda a identidade estabelecida de
       * quem já entrou na conta; o cookie novo é de uma visita anônima. Deixar
       * o anônimo vencer trocaria uma pessoa conhecida por uma desconhecida.
       *
       * O que se perde é a costura da landing para quem já era aluno antes de
       * 13/09/2026. Eram onze contas.
       */
      /*
       * O pacote de padrões atual do PostHog, que é o que a documentação manda
       * usar em projeto novo. Ele traz a captura de tela que acompanha troca de
       * rota; no modo manual ele não entra, para não brigar com a decisão que o
       * app já tomou.
       */
      ...(automatico ? { defaults: '2026-05-30' as const } : { capture_pageview: false }),
      disable_session_recording: !opcoes.gravarSessao,
      ...(opcoes.gravarSessao
        ? {
            session_recording: {
              maskAllInputs: true,
              maskInputOptions: { email: true, password: true, text: true },
              blockSelector: `[${ATRIBUTO_DE_MATERIAL_AUTORAL}]`,
              /*
               * **Escrito aqui, e não deixado para o painel do PostHog.** A
               * resposta da API é o próprio material - o comentário chega
               * inteiro no JSON -, então gravar o corpo das requisições
               * desfaria o bloqueio acima por outra porta. O painel hoje está
               * desligado; esta linha impede que alguém o ligue para depurar
               * uma tela e abra o acervo sem perceber.
               */
              recordBody: false,
              recordHeaders: false,
            },
          }
        : {}),
    });
    ativo = true;
    return true;
  } catch {
    // Analytics é auxiliar: uma falha no SDK não pode derrubar o produto.
    return false;
  }
}

export function posthogAtivo(): boolean {
  return ativo;
}

/**
 * Registra um evento, se o PostHog tiver subido.
 *
 * Baixo nível de propósito: quem dá nome e forma aos eventos é cada aplicação,
 * que conhece o próprio funil. O que mora aqui é só a guarda de "não subiu" e a
 * de "falhou" - as duas iguais para os dois lados, e as duas fáceis de esquecer
 * numa cópia.
 */
export function capturarEvento(nome: string, propriedades: Record<string, unknown> = {}): void {
  if (!ativo) return;

  try {
    posthog.capture(nome, propriedades);
  } catch {
    // Analytics é auxiliar: uma falha no SDK não pode derrubar o produto.
  }
}

/**
 * Os cabeçalhos que dizem à nossa API quem é esta pessoa para o PostHog.
 *
 * **Servem à sessão, não à identidade.** Quem costura a visita anônima à conta
 * é o `identify` que o app chama no login; o que o servidor não tem como saber
 * é em qual sessão a pessoa estava, e é isso que o `X-POSTHOG-SESSION-ID`
 * carrega. Sem ele, a compra que o webhook do Asaas confirma aparece solta, e
 * não dentro da navegação que levou a ela.
 *
 * Devolve objeto vazio quando o PostHog não subiu - o `fetch` que os usa
 * espalha o resultado, então ausência não vira cabeçalho vazio.
 */
export function cabecalhosDeCorrelacao(): Record<string, string> {
  if (!ativo) return {};

  try {
    const distinct = posthog.get_distinct_id();
    const sessao = posthog.get_session_id();

    return {
      ...(distinct ? { 'X-POSTHOG-DISTINCT-ID': distinct } : {}),
      ...(sessao ? { 'X-POSTHOG-SESSION-ID': sessao } : {}),
    };
  } catch {
    return {};
  }
}
